import React, { useState } from 'react'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps'
import { theme } from '../../styles/theme'
import { Card } from '../common/Card'
import { Globe } from 'lucide-react'

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

interface MarketData {
  country: string
  countryCode: string
  coordinates: [number, number]
  score: number
  marketSize: string
}

interface WorldMapCardProps {
  markets?: MarketData[]
  onCountryClick?: (countryCode: string) => void
}

export const WorldMapCard: React.FC<WorldMapCardProps> = ({
  markets,
  onCountryClick,
}) => {
  const [tooltipContent, setTooltipContent] = useState('')
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  const defaultMarkets: MarketData[] = [
    { country: 'Germany', countryCode: 'DE', coordinates: [10.4515, 51.1657], score: 8.7, marketSize: '$3.2B' },
    { country: 'France', countryCode: 'FR', coordinates: [2.2137, 46.2276], score: 8.5, marketSize: '$2.8B' },
    { country: 'Netherlands', countryCode: 'NL', coordinates: [5.2913, 52.1326], score: 8.2, marketSize: '$1.5B' },
    { country: 'Spain', countryCode: 'ES', coordinates: [-3.7492, 40.4637], score: 7.9, marketSize: '$2.1B' },
    { country: 'Italy', countryCode: 'IT', coordinates: [12.5674, 41.8719], score: 7.6, marketSize: '$1.9B' },
    { country: 'United Kingdom', countryCode: 'GB', coordinates: [-0.1276, 51.5074], score: 9.2, marketSize: '$4.5B' },
    { country: 'Sweden', countryCode: 'SE', coordinates: [18.0686, 59.3293], score: 7.8, marketSize: '$1.2B' },
    { country: 'Poland', countryCode: 'PL', coordinates: [21.0122, 52.2297], score: 7.4, marketSize: '$1.1B' },
  ]

  const marketsToShow = markets || defaultMarkets

  const getMarkerColor = (score: number) => {
    if (score >= 9) return theme.colors.success
    if (score >= 8) return theme.colors.successLight
    if (score >= 7) return theme.colors.warning
    return theme.colors.error
  }

  const getMarkerSize = (score: number) => {
    if (score >= 9) return 12
    if (score >= 8) return 10
    if (score >= 7) return 8
    return 6
  }

  return (
    <Card>
      <div style={{
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.bold,
        color: theme.colors.textPrimary,
        marginBottom: theme.spacing[4],
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing[2],
      }}>
        <Globe size={24} color={theme.colors.primary} />
        Global Market Opportunities
      </div>

      <div style={{ position: 'relative' }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 500,
            center: [10, 52],
          }}
          style={{
            width: '100%',
            height: '400px',
          }}
        >
          <ZoomableGroup>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={theme.colors.gray200}
                    stroke={theme.colors.gray300}
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: theme.colors.gray300, outline: 'none', cursor: 'pointer' },
                      pressed: { fill: theme.colors.gray400, outline: 'none' },
                    }}
                    onMouseEnter={(evt) => {
                      const { clientX, clientY } = evt
                      setTooltipContent(geo.properties.name)
                      setTooltipPosition({ x: clientX, y: clientY })
                    }}
                    onMouseLeave={() => {
                      setTooltipContent('')
                    }}
                  />
                ))
              }
            </Geographies>

            {marketsToShow.map((market) => (
              <Marker
                key={market.countryCode}
                coordinates={market.coordinates}
                onMouseEnter={(evt) => {
                  const { clientX, clientY } = evt
                  setTooltipContent(
                    `${market.country} - Score: ${market.score}/10 - ${market.marketSize}`
                  )
                  setTooltipPosition({ x: clientX, y: clientY })
                }}
                onMouseLeave={() => {
                  setTooltipContent('')
                }}
                onClick={() => onCountryClick?.(market.countryCode)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  r={getMarkerSize(market.score)}
                  fill={getMarkerColor(market.score)}
                  stroke={theme.colors.white}
                  strokeWidth={2}
                  style={{
                    transition: theme.transitions.fast,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.setAttribute('r', String(getMarkerSize(market.score) + 2))
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.setAttribute('r', String(getMarkerSize(market.score)))
                  }}
                />
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>

        {tooltipContent && (
          <div
            style={{
              position: 'fixed',
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 30,
              backgroundColor: theme.colors.gray900,
              color: theme.colors.white,
              padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.sm,
              pointerEvents: 'none',
              zIndex: theme.zIndex.popover,
              boxShadow: theme.shadows.lg,
              whiteSpace: 'nowrap',
            }}
          >
            {tooltipContent}
          </div>
        )}
      </div>

      <div style={{
        display: 'flex',
        gap: theme.spacing[4],
        marginTop: theme.spacing[4],
        paddingTop: theme.spacing[4],
        borderTop: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <div style={{
            width: 12,
            height: 12,
            borderRadius: theme.borderRadius.full,
            backgroundColor: theme.colors.success,
          }} />
          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
            High (9+)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <div style={{
            width: 10,
            height: 10,
            borderRadius: theme.borderRadius.full,
            backgroundColor: theme.colors.successLight,
          }} />
          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
            Good (8-9)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: theme.borderRadius.full,
            backgroundColor: theme.colors.warning,
          }} />
          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
            Fair (7-8)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <div style={{
            width: 6,
            height: 6,
            borderRadius: theme.borderRadius.full,
            backgroundColor: theme.colors.error,
          }} />
          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
            Low (&lt;7)
          </span>
        </div>
      </div>
    </Card>
  )
}
